// DbIdBadge — shows an item's `db_id` (`fam:35` / `line:239` / `prod:21`) with a
// copy button. Alex, 2026-07-16: "Can you add the id in the catalog & Product
// that I can find" — you cannot stamp into Onshape what you cannot read out of
// the DB, and hunting for it in a URL is not a workflow.
//
// WHY COPY AND NOT JUST TEXT: a db_id gets RETYPED into Onshape by hand (RULES 10
// — no writes until Alex authorizes the first set), and a typo lands on a
// real-but-WRONG row. Our three id spaces overlap 145 times: `prod:21` is "Spray
// 4 Risers", `line:21` is "Duct Joint / Sealant" — both exist, both price, and
// nothing errors. That is the one failure mode that would be invisible.
//
// CLIPBOARD IN AN IFRAME: the Register panel runs inside Onshape as a
// cross-origin iframe, where `navigator.clipboard` is blocked unless the host
// sets `allow="clipboard-write"` (Onshape's markup, not ours). So: try the async
// API, fall back to execCommand (which an iframe does allow), and if both fail
// SAY SO rather than silently doing nothing — the previous version swallowed the
// rejection in an empty catch, which is how Alex found it ("the copy does not
// work"). The value is also plain selectable text, so manual select-copy always
// works as a last resort.
//
// `variant`/`mono` (2026-07-20, UI_VISION §10 "Id badge" fork closure): absorbed
// from OnshapePanel's `IdRow`, which this replaces. Panel mode needed two things
// this component didn't have — not a missing copy affordance (it already had
// one, same mechanism) but a LAYOUT: a full-width labeled row (for a list of
// candidate families, one per line, label column aligned) with a larger,
// wrapping value, versus this component's inline chip. `variant="row"` gives
// that layout; `mono={false}` un-monos the value, needed when the row is really
// showing a family name next to its id rather than the id being the headline.

import { useState, useRef } from 'react';

export default function DbIdBadge({ dbId, label = 'db_id', hint, style, mono = true, variant = 'chip' }) {
  const [state, setState] = useState('idle'); // idle | copied | failed
  const valueRef = useRef(null);
  if (!dbId) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(dbId);
      setState('copied');
    } catch {
      try {
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(valueRef.current);
        sel.removeAllRanges();
        sel.addRange(range);
        setState(document.execCommand('copy') ? 'copied' : 'failed');
        sel.removeAllRanges();
      } catch { setState('failed'); }
    }
    setTimeout(() => setState('idle'), 1600);
  };

  const isRow = variant === 'row';

  return (
    <span style={{
      display: isRow ? 'flex' : 'inline-flex', alignItems: 'center', gap: isRow ? 6 : 5,
      marginBottom: isRow ? 3 : undefined, ...style,
    }}>
      <span style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-secondary)', minWidth: isRow ? 60 : undefined }}>{label}</span>
      <code
        ref={valueRef}
        style={{
          fontFamily: mono ? 'var(--ff-mono)' : 'inherit', fontSize: isRow ? 'var(--fs-body)' : 'var(--fs-small)',
          color: 'var(--th-text-body)', userSelect: 'all',
          wordBreak: isRow ? 'break-all' : undefined, flex: isRow ? 1 : undefined,
        }}
      >{dbId}</code>
      <button
        onClick={copy}
        title={`Copy ${dbId} — paste into the Onshape part's db_id property`}
        style={{
          fontSize: isRow ? 'var(--fs-small)' : 'var(--fs-micro)', padding: isRow ? '2px 8px' : '1px 6px',
          borderRadius: 3, cursor: 'pointer', flexShrink: 0,
          border: '1px solid var(--th-border-subtle)', background: 'var(--th-panel-bg)',
          color: state === 'failed' ? 'var(--th-status-deprecated)' : 'var(--th-text-secondary)',
        }}
      >{state === 'copied' ? '✓ copied' : state === 'failed' ? '⚠ select it' : 'copy'}</button>
      {hint && <span style={{ fontSize: isRow ? 'var(--fs-small)' : 'var(--fs-micro)', color: 'var(--th-text-hint)' }}>{hint}</span>}
    </span>
  );
}
